<template>
  <v-card class="pa-6">
    <v-card-title class="text-uppercase">
      {{ isEditing ? 'Editar' : 'Agregar' }} servicio
    </v-card-title>
    <hr class="my-3" />
    <v-card-text>
      <v-form ref="serviceForm" v-model="formValid">
        <v-row class="my-1" v-if="!isEditing">
          <v-col cols="12">
            <v-btn-toggle v-model="serviceType" mandatory dense>
              <v-btn value="own" small>Servicio propio</v-btn>
              <v-btn value="catalog" small>Variante de catálogo</v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <template v-if="isPricingOnlyEdit">
          <v-row class="my-1">
            <v-col cols="12">
              <div class="text-caption grey--text mb-1">Variante de catálogo</div>
              <div class="text-body-2 font-weight-medium">{{ service.title }}</div>
            </v-col>
          </v-row>
          <v-row class="my-1">
            <v-col cols="4">
              <v-text-field
                label="Precio"
                v-model.number="form.pricing"
                :rules="pricingRules"
                type="number"
                required
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
        </template>

        <template v-else>
          <v-row class="my-1" v-if="isCatalogVariant">
            <v-col cols="8">
              <v-autocomplete
                label="Servicios del catálogo Pawer"
                v-model="form.serviceIds"
                :items="catalogServices"
                :loading="loadingCatalogServices"
                item-text="title"
                item-value="id"
                :rules="serviceIdsRules"
                multiple
                chips
                deletable-chips
                required
                outlined
                dense
                hide-details="auto"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row class="my-1">
            <v-col cols="6">
              <v-text-field
                label="Título"
                v-model="form.title"
                :rules="requiredRules"
                required
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                label="Precio"
                v-model.number="form.pricing"
                :rules="pricingRules"
                type="number"
                required
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="3" class="d-flex align-center" v-if="!isCatalogVariant">
              <v-switch
                class="mt-0 pt-0"
                label="Visible"
                v-model="form.visible"
                hide-details="auto"
              ></v-switch>
            </v-col>
          </v-row>
          <v-row class="my-1">
            <v-col cols="8">
              <v-text-field
                label="URL de ícono (opcional)"
                v-model="form.iconUrl"
                outlined
                dense
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
        </template>
      </v-form>
    </v-card-text>
    <hr class="my-3" />
    <v-card-actions class="justify-end">
      <v-btn text color="primary" rounded @click="closeDialog">Cancelar</v-btn>
      <v-btn :disabled="!formValid || saving" :loading="saving" color="primary" rounded @click="save">
        Guardar
      </v-btn>
    </v-card-actions>

    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import ClinicsService from '../services/clinics.service';
import ServicesService from '../services/services.service';
import { utilsMixin } from '../mixins/utilsMixin';

// El backend ya valida SERVICE_ID_REQUIRED/TITLE_REQUIRED/PRICING_REQUIRED (DomainError, mismo
// patrón que el resto de application/clinics en pawer-backend) — este diccionario solo traduce
// esos códigos, sin duplicar la regla de negocio acá.
const SERVICE_ERROR_MESSAGES = {
  SERVICE_ID_REQUIRED: 'Selecciona un servicio del catálogo.',
  TITLE_REQUIRED: 'El título es obligatorio.',
  PRICING_REQUIRED: 'El precio es obligatorio.',
  DUPLICATE_SERVICE_IDS: 'No puedes seleccionar el mismo servicio del catálogo más de una vez.'
};

export default {
  name: 'ClinicServiceModal',

  mixins: [utilsMixin],

  props: {
    clinicId: { type: String, required: true },
    service: { type: Object, default: null }
  },

  data() {
    return {
      formValid: false,
      saving: false,
      serviceType: 'own',
      catalogServices: [],
      loadingCatalogServices: false,
      form: this.emptyForm(),
      requiredRules: [v => !!v || 'El campo es requerido'],
      serviceIdsRules: [
        v => (Array.isArray(v) && v.length > 0) || 'Selecciona al menos un servicio del catálogo.'
      ],
      pricingRules: [
        v => (v !== null && v !== '' && v !== undefined) || 'El precio es requerido',
        v => Number(v) > 0 || 'El precio debe ser mayor a 0'
      ],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },

  computed: {
    isEditing() {
      return this.service !== null;
    },
    isCatalogVariant() {
      return this.isEditing
        ? this.service.serviceId !== null && this.service.serviceId !== undefined
        : this.serviceType === 'catalog';
    },
    isPricingOnlyEdit() {
      return this.isEditing && this.isCatalogVariant;
    }
  },

  watch: {
    service() {
      this.form = this.emptyForm();
      this.parseService();
    },
    serviceType(value) {
      if (value === 'catalog' && !this.catalogServices.length) this.fetchCatalogServices();
    }
  },

  mounted() {
    this.parseService();
  },

  methods: {
    emptyForm() {
      return { serviceIds: [], title: '', pricing: null, iconUrl: '', visible: true };
    },

    parseService() {
      if (this.service) {
        this.form = {
          serviceIds: this.service.serviceId ? [this.service.serviceId] : [],
          title: this.service.title ?? '',
          pricing: this.service.pricing ?? null,
          iconUrl: this.service.iconUrl ?? '',
          visible: this.service.visible ?? true
        };
      } else {
        this.form = this.emptyForm();
        if (this.serviceType === 'catalog') this.fetchCatalogServices();
      }
      this.$nextTick(() => {
        if (this.$refs.serviceForm) this.$refs.serviceForm.resetValidation();
      });
    },

    async fetchCatalogServices() {
      this.loadingCatalogServices = true;
      const res = await ServicesService.getAllServices();
      if (res.status === 'ok') this.catalogServices = res.data;
      this.loadingCatalogServices = false;
    },

    closeDialog() {
      this.$emit('close-dialog');
    },

    async save() {
      if (!this.$refs.serviceForm.validate()) return;

      this.saving = true;
      const response = this.isEditing ? await this.updateService() : await this.createService();
      this.saving = false;

      if (response.status === 'ok') {
        this.$emit('update-list');
        this.closeDialog();
      } else {
        this.showErrorSnackbar(this.mapServiceError(response));
      }
    },

    async updateService() {
      if (this.isCatalogVariant) {
        return ClinicsService.updateServicePricing(this.clinicId, this.service.id, this.form.pricing);
      }
      return ClinicsService.updateOwnService(this.clinicId, this.service.id, {
        title: this.form.title,
        pricing: this.form.pricing,
        iconUrl: this.form.iconUrl || null,
        visible: this.form.visible
      });
    },

    async createService() {
      if (this.serviceType === 'catalog') {
        return ClinicsService.createCatalogService(this.clinicId, {
          serviceIds: this.form.serviceIds,
          title: this.form.title,
          pricing: this.form.pricing,
          iconUrl: this.form.iconUrl || null
        });
      }
      return ClinicsService.createOwnService(this.clinicId, {
        title: this.form.title,
        pricing: this.form.pricing,
        iconUrl: this.form.iconUrl || null,
        visible: this.form.visible
      });
    },

    mapServiceError(response) {
      if (response.httpCode === 404) return 'El servicio de catálogo indicado no existe.';
      if (response.httpCode === 409) return 'Ya existe una variante con ese nombre para este servicio.';
      return (
        SERVICE_ERROR_MESSAGES[response.message] ||
        'Ocurrió un error al guardar el servicio. Intenta nuevamente.'
      );
    }
  }
};
</script>
